import React, { useState } from 'react'
import { View, FlatList, TouchableOpacity, Image, Text as RNText, TextInput } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Text, Screen, Icon, HiddenText, KeyboardAwareView } from '@components'
import { getAddConversationStyles } from './style'
import { ConversationHeader } from './components/ConversationHeader'
import { AddConversationProps } from './types'
import { Scope } from '@utils'
import useAddConversationCtrl from './useAddConversationCtrl'
import {
  Contact,
  useInitiateGroupConversation,
  useInitiateIndividualConversation,
  usePaginateContacts,
  useScreen,
} from '@hooks'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'

export const AddConversation: React.FC<AddConversationProps> = () => {
  const theme = useTheme()
  const { isWeb } = useScreen()
  const { t } = useLanguage()
  const styles = getAddConversationStyles(theme)

  const { goBack, goToConversation, goToGroupConversation } = useAddConversationCtrl()

  const [searchText, setSearchText] = useState('')
  const [groupName, setGroupName] = useState('')
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([])
  const [message, setMessage] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<Scope>(Scope.PRIVATE)

  const { flatData: contacts } = usePaginateContacts({
    search: searchText,
    scope: selectedFilter === Scope.PRIVATE ? Scope.PRIVATE : Scope.PROFESSIONAL,
    noGhost: true,
  })
  const { mutateAsync: initiateIndividualConversation } = useInitiateIndividualConversation()
  const { mutateAsync: initiateGroupConversation } = useInitiateGroupConversation()

  const [height, setHeight] = useState(40)

  const handleContactPress = (contact: Contact) => {
    // Vérifier si le contact est déjà sélectionné
    const isAlreadySelected = selectedContacts.find((c) => c.id === contact.id)

    if (isAlreadySelected) {
      // Désélectionner le contact
      setSelectedContacts(selectedContacts.filter((c) => c.id !== contact.id))
    } else {
      // Ajouter le contact à la liste des sélectionnés
      setSelectedContacts([...selectedContacts, contact])
    }
  }

  const handleRemoveContact = (contactId: string) => {
    setSelectedContacts(selectedContacts.filter((c) => c.id !== contactId))
  }

  const sendMessage = async () => {
    if (selectedContacts.length > 0) {
      if (selectedContacts.length === 1) {
        const conversation = await initiateIndividualConversation({
          participantAccountId: selectedContacts[0].accountId,
          message: message,
        })
        goToConversation(conversation.id)
      } else {
        const conversation = await initiateGroupConversation({
          participants: selectedContacts.map((c) => ({
            accountId: c.accountId,
            role: 'member',
          })),
          message: message,
        })
        goToGroupConversation(conversation.id)
      }
      setMessage('')
      setGroupName('')
      setSelectedContacts([])
    }
  }

  const renderTabButton = (tab: Scope, title: string, icon: string, color: string) => (
    <TouchableOpacity
      style={[styles.tabButton, selectedFilter === tab && styles.activeTabButton]}
      onPress={() => setSelectedFilter(tab)}
    >
      <Icon
        name={icon as any}
        size={16}
        color={selectedFilter === tab ? color : theme.colors.greyBlue}
      />
      <RNText
        style={[styles.tabText, selectedFilter === tab && { color: color, fontWeight: 'bold' }]}
      >
        {title}
      </RNText>
      {selectedFilter === tab && (
        <View style={[styles.activeTabIndicator, { backgroundColor: color }]} />
      )}
    </TouchableOpacity>
  )

  // Rendu des contacts (style ContactSearch simple)
  const renderContactItem = ({ item }: { item: Contact }) => {
    const isSelected = selectedContacts.find((c) => c.id === item.id)

    return (
      <TouchableOpacity
        style={styles.contactItem}
        onPress={() => handleContactPress(item)}
        activeOpacity={0.7}
      >
        <Image
          source={item.avatarUrl ? { uri: item.avatarUrl } : defaultImage}
          style={styles.contactAvatar}
        />
        <View style={styles.contactInfo}>
          <Text type="body" style={styles.contactName}>
            {item.fullName}
          </Text>
        </View>

        {/* Checkbox */}
        <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
          {isSelected && <Icon name="check" size={14} color={theme.colors.white} />}
        </View>

        {/* Barre de sélection verticale */}
        <View style={[styles.selectionBar, isSelected && styles.selectionBarActive]} />
      </TouchableOpacity>
    )
  }

  return (
    <Screen>
      <KeyboardAwareView isFullScreen offsetTopInset>
        <ConversationHeader
          onBackPress={goBack}
          searchValue={searchText}
          onSearchChange={setSearchText}
          groupName={groupName}
          onGroupNameChange={setGroupName}
          selectedContacts={selectedContacts}
          onRemoveContact={handleRemoveContact}
          showPeopleIcon={true}
          showSearchInput={true}
        />

        <View style={styles.tabContainer}>
          {renderTabButton(Scope.PRIVATE, t('messagesScreen.private'), 'lock', theme.colors.green)}
          {renderTabButton(
            Scope.PROFESSIONAL,
            t('messagesScreen.pro'),
            'work',
            theme.colors.primary,
          )}
        </View>

        {/* Compteur de résultats uniforme */}
        <RNText style={styles.sectionTitle}>
          {t('common.contactCount', { count: contacts.length })}
        </RNText>

        <View style={styles.container}>
          {/* Liste des contacts sans séparateurs */}
          <FlatList
            data={contacts}
            renderItem={renderContactItem}
            keyExtractor={(item) => item.id ?? ''}
            style={styles.contactsList}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.contactsContent}
          />

          {/* Input en bas (style Conversation) */}
          <View
            style={[
              styles.inputContainer,
              {
                opacity: selectedContacts.length > 0 ? 1 : 0.5,
                pointerEvents: selectedContacts.length > 0 ? 'auto' : 'none',
              },
            ]}
          >
            <HiddenText text={message} setHeight={setHeight} />
            <View style={styles.inputWrapper}>
              <TextInput
                style={[styles.textInput, isWeb && { height }]}
                value={message}
                onChangeText={setMessage}
                placeholder={t('messaging.messagePlaceholder')}
                placeholderTextColor={theme.colors.greyBlue}
                multiline
              />
              <TouchableOpacity
                style={[styles.sendButton, { backgroundColor: theme.colors.green }]}
                onPress={sendMessage}
              >
                <Icon name="send" size={20} color={theme.colors.white} />
              </TouchableOpacity>
            </View>
            <TouchableOpacity
              style={[styles.imageButton, { backgroundColor: theme.colors.green + '20' }]}
            >
              <Icon name="picture" size={20} color={theme.colors.green} />
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAwareView>
    </Screen>
  )
}
